import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import Button from 'kingpin/atoms/Button';
import Tooltip from 'components/Tooltip';
import Typography from 'kingpin/atoms/Typography';
import ErrorBoundary from 'components/Common/ErrorBoundary';
import InsightsChatConversation from 'components/InsightsChat/InsightsChatConversation';
import InsightsConversationList from 'components/InsightsChat/InsightsConversationList';
import DownloadChatLogButton from 'components/InsightsChat/downloadChatLog/DownloadChatLogButton';
import InsightsMemoryModal from 'components/InsightsChat/memory/InsightsMemoryModal';
import { InsightsChatContext } from 'contextProviders/InsightsChatProvider';
import useInsightsChatBudget from 'hooks/useInsightsChatBudget';
import ToastContext from 'contexts/ToastContext';
import AppRoutes from 'navigation/appRoutes';

// Full-page top-level AI Insights chat: conversation history in a persistent
// left column, the active chat as the primary view. Shares InsightsChatProvider
// state with the contextual slideout, so history and usage stay consistent.

const ScreenDiv = styled.div`
  display: flex;
  height: 100%;
  background-color: var(--color-bg-primary);
`;

const Sidebar = styled.div`
  width: 300px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  min-height: 0;
  border-right: 1px solid var(--color-border-secondary);
  padding: 16px;
  box-sizing: border-box;
`;

const SidebarHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
`;

const Main = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  padding: 16px 20px;
  box-sizing: border-box;
`;

const MainHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin-bottom: 16px;
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const UsageLabel = styled.div`
  color: var(--color-text-secondary);
  white-space: nowrap;
  padding: 3px 10px;
  border-radius: 999px;
  background-color: var(--color-bg-secondary);
  border: 1px solid var(--color-border-secondary);
`;

const formatUsd = (value: number): string => `$${value.toFixed(2)}`;

const InsightsChatScreen = () => {
  const { startNewChat, refreshConversations, usageSpentUsd, budgetUsd } =
    useContext(InsightsChatContext);
  const [showMemory, setShowMemory] = useState<boolean>(false);

  // On navigating to the page, start from a blank chat (past chats stay in the
  // left column to load on demand) and load the recent list — the provider only
  // auto-loads the recent list when the slideout opens.
  useEffect(() => {
    startNewChat();
    refreshConversations();
  }, [startNewChat, refreshConversations]);

  return (
    <ScreenDiv data-testid="insights-chat-screen">
      <Sidebar>
        <SidebarHeader>
          <Typography.Header type="H4">Chats</Typography.Header>
          <Button
            type="Secondary"
            size="Small"
            icon="note-stack-add"
            label="New chat"
            onClick={() => {
              startNewChat();
              refreshConversations();
            }}
            testId="insights-page-new-chat"
          />
        </SidebarHeader>
        <InsightsConversationList />
      </Sidebar>
      <Main>
        <MainHeader>
          <HeaderRight>
            {budgetUsd !== null && (
              <UsageLabel data-testid="insights-usage">
                <Typography.Body type="Body 12">
                  {usageSpentUsd != null ? formatUsd(usageSpentUsd) : '—'} /{' '}
                  {formatUsd(budgetUsd)} this month
                </Typography.Body>
              </UsageLabel>
            )}
            <DownloadChatLogButton />
            <Tooltip content="Memory">
              <Button
                type="Ghost"
                size="Small"
                icon="database"
                onClick={() => setShowMemory(true)}
                testId="insights-memory-open"
              />
            </Tooltip>
          </HeaderRight>
        </MainHeader>
        <InsightsChatConversation />
      </Main>
      {showMemory && (
        <InsightsMemoryModal onClose={() => setShowMemory(false)} />
      )}
    </ScreenDiv>
  );
};

const Gate = () => {
  const { isEligible } = useInsightsChatBudget();
  const { showToast } = useContext(ToastContext);
  const navigate = useNavigate();

  // Eligibility (budget) is the per-user check; the account-level FeatureGate
  // around the route is the master switch.
  useEffect(() => {
    if (isEligible) {
      return;
    }
    showToast(
      'This screen is not available to you. We have redirected you home.',
    );
    navigate(AppRoutes.home);
  }, [isEligible, navigate, showToast]);

  if (!isEligible) {
    return null;
  }

  return (
    <ErrorBoundary isRetryable>
      <InsightsChatScreen />
    </ErrorBoundary>
  );
};

export default Gate;
